import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type { Tramite, TramiteAdjunto, TramiteEvento, TramiteTipo } from '../types';

export const BUCKET_ADJUNTOS = 'tramites-adjuntos';
export const MAX_ADJUNTOS = 5;
export const MAX_BYTES = 10 * 1024 * 1024;
export const MIME_PERMITIDOS = ['image/png', 'image/jpeg', 'application/pdf'];

/** Devuelve un mensaje si el archivo no se puede adjuntar, o null si esta bien. */
export function validarArchivo(file: { name: string; size: number; type: string }): string | null {
  if (!MIME_PERMITIDOS.includes(file.type)) return `${file.name}: solo se aceptan PDF, PNG o JPG.`;
  if (file.size > MAX_BYTES) return `${file.name}: supera los 10 MB.`;
  if (file.size === 0) return `${file.name}: el archivo está vacío.`;
  return null;
}

export function useTramiteTipos() {
  return useQuery({
    queryKey: ['tramite-tipos'],
    queryFn: async () => {
      const { data, error } = await supabase.from('tramite_tipos').select('*').eq('activo', true).order('orden');
      if (error) throw error;
      return (data ?? []) as TramiteTipo[];
    },
  });
}

export function useMisTramites() {
  return useQuery({
    queryKey: ['mis-tramites'],
    queryFn: async () => {
      const { data, error } = await supabase.from('vw_mis_tramites').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Tramite[];
    },
  });
}

export function useTramiteEventos(tramiteId: string | undefined) {
  return useQuery({
    queryKey: ['tramite-eventos', tramiteId],
    enabled: !!tramiteId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vw_mis_tramite_eventos')
        .select('*')
        .eq('tramite_id', tramiteId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as TramiteEvento[];
    },
  });
}

export function useTramiteAdjuntos(tramiteId: string | undefined) {
  return useQuery({
    queryKey: ['tramite-adjuntos', tramiteId],
    enabled: !!tramiteId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vw_mis_tramite_adjuntos')
        .select('*')
        .eq('tramite_id', tramiteId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as TramiteAdjunto[];
    },
  });
}

/**
 * Sube los archivos a la carpeta del afiliado y devuelve los paths para la RPC.
 * Limite conocido: si la RPC falla despues de subir, los archivos quedan huerfanos
 * en el bucket (el afiliado no puede borrarlos).
 */
async function subirAdjuntos(afiliadoId: string, files: File[]): Promise<{ path: string; nombre: string }[]> {
  const carpeta = crypto.randomUUID();
  const out: { path: string; nombre: string }[] = [];
  for (const file of files) {
    const safe = file.name.replace(/[^\w.-]+/g, '_');
    const path = `${afiliadoId}/${carpeta}/${safe}`;
    const { error } = await supabase.storage.from(BUCKET_ADJUNTOS).upload(path, file, { contentType: file.type, upsert: false });
    if (error) throw new Error(`No se pudo subir ${file.name}: ${error.message}`);
    out.push({ path, nombre: file.name });
  }
  return out;
}

export interface CrearTramiteArgs {
  afiliadoId: string;
  tipoId: string;
  detalle: string;
  files: File[];
}

export function useCrearTramite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ afiliadoId, tipoId, detalle, files }: CrearTramiteArgs) => {
      const adjuntos = await subirAdjuntos(afiliadoId, files);
      const { data, error } = await supabase.rpc('crear_tramite', {
        p_tipo_id: tipoId,
        p_detalle: detalle,
        p_adjuntos: adjuntos,
      });
      if (error) throw new Error(error.message);
      return data as { id: string; numero: number };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mis-tramites'] }),
  });
}

export interface ResponderTramiteArgs {
  afiliadoId: string;
  tramiteId: string;
  mensaje: string;
  files: File[];
}

export function useResponderTramite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ afiliadoId, tramiteId, mensaje, files }: ResponderTramiteArgs) => {
      const adjuntos = await subirAdjuntos(afiliadoId, files);
      const { error } = await supabase.rpc('responder_tramite', {
        p_tramite_id: tramiteId,
        p_mensaje: mensaje,
        p_adjuntos: adjuntos,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['mis-tramites'] });
      qc.invalidateQueries({ queryKey: ['tramite-eventos', v.tramiteId] });
      qc.invalidateQueries({ queryKey: ['tramite-adjuntos', v.tramiteId] });
    },
  });
}

/** URL firmada de corta duracion para abrir un adjunto. */
export async function urlAdjunto(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET_ADJUNTOS).createSignedUrl(path, 300);
  if (error || !data?.signedUrl) throw new Error(error?.message ?? 'No se pudo abrir el archivo.');
  return data.signedUrl;
}
